import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

// NOTE: Default password is intentionally blank — the admin creating a new
// team leader must explicitly set one, avoiding any hardcoded shared default.
const EMPTY_LEADER = { full_name: '', department: 'Media', username: '', password: '' };

function DepartmentGrid({ departments }) {
  return (
    <div className="card-moj p-5">
      <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Church Departments</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        {departments.map((d) => (
          <div key={d.id} className="bg-moj-cream rounded-lg p-3">
            <div className="font-semibold text-moj-purple-deep">{d.name}</div>
            <div className="text-xs text-moj-charcoal/70">{d.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamLeaderForm({ form, setForm, onSubmit, isSubmitting, departments }) {
  return (
    <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-3 mb-4 p-4 bg-moj-cream rounded-lg" data-testid="tl-form">
      <input className="input-moj" placeholder="Full name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}/>
      <select className="input-moj" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
        {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
      </select>
      <input className="input-moj" placeholder="Username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}/>
      <input className="input-moj" type="password" placeholder="Set a strong password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}/>
      <button type="submit" disabled={isSubmitting} className="btn-primary sm:col-span-2">Create</button>
    </form>
  );
}

function TeamLeaderRow({ leader, onDeactivate }) {
  return (
    <div className="py-3 flex items-center justify-between">
      <div>
        <div className="font-semibold">
          {leader.full_name} <span className="text-xs text-moj-gold-deep">({leader.department})</span>
        </div>
        <div className="text-xs text-moj-charcoal/60">@{leader.username} • {leader.active ? 'Active' : 'Inactive'}</div>
      </div>
      {leader.active && (
        <button onClick={() => onDeactivate(leader.id)} className="text-xs px-3 py-1 rounded bg-gray-500 text-white">
          Deactivate
        </button>
      )}
    </div>
  );
}

export default function DepartmentsTab() {
  const qc = useQueryClient();
  const { data: departments = [] } = useQuery({ queryKey: ['adm-depts'], queryFn: async () => (await api.get('/admin/departments')).data });
  const { data: leaders = [] } = useQuery({ queryKey: ['adm-leaders'], queryFn: async () => (await api.get('/admin/team-leaders')).data });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_LEADER);

  const create = useMutation({
    mutationFn: async (p) => (await api.post('/admin/team-leaders', p)).data,
    onSuccess: () => {
      toast.success('Team leader created');
      qc.invalidateQueries({ queryKey: ['adm-leaders'] });
      setShowForm(false);
      setForm(EMPTY_LEADER);
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not create'),
  });
  const deactivate = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/team-leaders/${id}`)).data,
    onSuccess: () => { toast.success('Deactivated'); qc.invalidateQueries({ queryKey: ['adm-leaders'] }); },
  });

  const onSubmit = (e) => { e.preventDefault(); create.mutate(form); };

  return (
    <div className="space-y-6">
      <DepartmentGrid departments={departments}/>
      <div className="card-moj p-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-serif text-lg text-moj-purple-deep">Team Leaders</h3>
          <button onClick={() => setShowForm((s) => !s)} className="btn-gold text-sm py-2 px-3" data-testid="new-tl-btn">
            <Plus size={14}/> Add
          </button>
        </div>
        {showForm && (
          <TeamLeaderForm form={form} setForm={setForm} onSubmit={onSubmit}
            isSubmitting={create.isPending} departments={departments}/>
        )}
        <div className="divide-y">
          {leaders.map((l) => (
            <TeamLeaderRow key={l.id} leader={l} onDeactivate={deactivate.mutate}/>
          ))}
        </div>
      </div>
    </div>
  );
}
